import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExternalLink,
  Loader2,
  MessageSquareText,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useInterval } from '@/hooks/useInterval';
import { isPmCapableRunner } from '@/lib/gift-agent';
import { NativeTalkChat } from '@/components/talk/NativeTalkChat';
import { useTalkSessionStore } from '@/components/talk/useTalkSessionStore';
import { cn } from '@/lib/utils';
import { useTalkSessionData } from '@/components/talk/session/useTalkSessionData';
import { TalkSessionHeader } from '@/components/talk/session/TalkSessionHeader';
import { TalkSessionBottomNav } from '@/components/talk/session/TalkSessionBottomNav';
import { ThreadsPanel } from '@/components/talk/session/ThreadsPanel';
import { HistoryPanel } from '@/components/talk/session/HistoryPanel';
import type { GitCommit } from '@claude-code-api/shared';

type PendingGitConfirm =
  | { kind: 'jump'; commit: GitCommit }
  | { kind: 'return' }
  | null;

export default function TalkSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const {
    session,
    accessInfo,
    loading,
    chatSessions,
    activeChatSessionId,
    chatSessionsLoading,
    actionLoading,
    chatRefreshKey,
    gitState,
    gitHistoryLoading,
    gitHistoryError,
    fetchSession,
    fetchChatSessions,
    fetchGitHistory,
    createChatSession,
    activateChatSession,
    deleteChatSession,
    bulkDeleteChatSessions,
    commitRename,
    runGitAction,
    jumpToCommit,
    jumpBack,
  } = useTalkSessionData(String(sessionId || ''));

  const activePanel = useTalkSessionStore((state) => state.activePanel);
  const threadSearch = useTalkSessionStore((state) => state.threadSearch);
  const visibleThreadCount = useTalkSessionStore(
    (state) => state.visibleThreadCount,
  );
  const historySearch = useTalkSessionStore((state) => state.historySearch);
  const visibleHistoryCount = useTalkSessionStore(
    (state) => state.visibleHistoryCount,
  );
  const selectedThreadIds = useTalkSessionStore(
    (state) => state.selectedThreadIds,
  );
  const renamingSessionId = useTalkSessionStore(
    (state) => state.renamingSessionId,
  );
  const renamingValue = useTalkSessionStore((state) => state.renamingValue);
  const setActivePanel = useTalkSessionStore((state) => state.setActivePanel);
  const setThreadSearch = useTalkSessionStore((state) => state.setThreadSearch);
  const setVisibleThreadCount = useTalkSessionStore(
    (state) => state.setVisibleThreadCount,
  );
  const setHistorySearch = useTalkSessionStore(
    (state) => state.setHistorySearch,
  );
  const setVisibleHistoryCount = useTalkSessionStore(
    (state) => state.setVisibleHistoryCount,
  );
  const toggleSelectedThreadId = useTalkSessionStore(
    (state) => state.toggleSelectedThreadId,
  );
  const clearSelectedThreadIds = useTalkSessionStore(
    (state) => state.clearSelectedThreadIds,
  );
  const setSelectedThreadIds = useTalkSessionStore(
    (state) => state.setSelectedThreadIds,
  );
  const setRenamingSessionId = useTalkSessionStore(
    (state) => state.setRenamingSessionId,
  );
  const setRenamingValue = useTalkSessionStore(
    (state) => state.setRenamingValue,
  );
  const resetThreadWindow = useTalkSessionStore(
    (state) => state.resetThreadWindow,
  );
  const resetHistoryWindow = useTalkSessionStore(
    (state) => state.resetHistoryWindow,
  );
  const resetForSession = useTalkSessionStore((state) => state.resetForSession);
  const [pendingGitConfirm, setPendingGitConfirm] =
    useState<PendingGitConfirm>(null);

  useEffect(() => {
    resetForSession();
  }, [resetForSession, sessionId]);

  useInterval(
    () => {
      void fetchSession(true);
      void fetchChatSessions();
    },
    session?.status === 'running' ? 30000 : null,
  );

  const activeChatName = useMemo(() => {
    return (
      chatSessions.find((item) => item.id === activeChatSessionId)?.name ||
      'PM chat'
    );
  }, [activeChatSessionId, chatSessions]);
  const previewUrl =
    accessInfo?.previewUrls.port5173 ||
    accessInfo?.previewUrls.port3000 ||
    null;
  const openJumpConfirm = (commit: GitCommit) =>
    setPendingGitConfirm({ kind: 'jump', commit });
  const openReturnConfirm = () => setPendingGitConfirm({ kind: 'return' });
  const requiresGitConfirm = !!gitState?.dirty;

  const handleJumpToCommit = (commit: GitCommit) => {
    if (
      gitState?.travelState?.mode === 'travel' &&
      gitState.travelState.originHead === commit.hash
    ) {
      handleJumpBack();
      return;
    }
    if (requiresGitConfirm) {
      openJumpConfirm(commit);
      return;
    }
    void jumpToCommit(commit);
  };

  const handleJumpBack = () => {
    if (requiresGitConfirm) {
      openReturnConfirm();
      return;
    }
    void jumpBack();
  };

  const headerContent = useMemo(() => {
    if (activePanel === 'sessions') {
      return {
        title: 'Threads',
        subtitle: 'Switch, rename, search, and manage PM conversation threads.',
      };
    }

    if (activePanel === 'history') {
      return {
        title: 'Versions',
        subtitle:
          gitState?.travelState?.mode === 'travel'
            ? 'You are viewing an older version. Restore it as the latest, browse another version, or go back to current.'
            : 'Browse past versions, restore one as the latest, and save your current work with one click.',
      };
    }

    return {
      title: 'Chat',
      subtitle: activeChatName,
    };
  }, [activeChatName, activePanel]);

  const headerActionSlot = useMemo(() => {
    if (activePanel === 'sessions') {
      return (
        <>
          {selectedThreadIds.length > 0 ? (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                void bulkDeleteChatSessions(selectedThreadIds).then(() => {
                  clearSelectedThreadIds();
                });
              }}
              disabled={!!actionLoading}
            >
              {actionLoading === 'bulk-delete' ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : null}
              Delete ({selectedThreadIds.length})
            </Button>
          ) : null}
          <Button
            size="sm"
            onClick={() => {
              void createChatSession().then((createdChatSessionId) => {
                if (!createdChatSessionId) return;
                clearSelectedThreadIds();
                setActivePanel('chat');
              });
            }}
            disabled={actionLoading === 'create'}
          >
            {actionLoading === 'create' ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-1.5 h-4 w-4" />
            )}
            New
          </Button>
        </>
      );
    }

    if (activePanel === 'history') {
      const primaryHistoryAction =
        gitState?.travelState?.mode === 'travel' ? 'save' : 'publish';
      const isPrimaryHistoryActionLoading =
        actionLoading === primaryHistoryAction;
      return (
        <>
          {gitState?.travelState?.mode === 'travel' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleJumpBack}
              disabled={!!actionLoading}
              className="shrink-0"
            >
              {actionLoading === 'return' ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : null}
              Back to current
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchGitHistory()}
            className="shrink-0"
          >
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void runGitAction(primaryHistoryAction)}
            disabled={!!actionLoading}
            className="shrink-0"
          >
            {isPrimaryHistoryActionLoading ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : null}
            {gitState?.travelState?.mode === 'travel'
              ? 'Restore as latest'
              : 'Save'}
          </Button>
        </>
      );
    }

    if (activePanel === 'chat') {
      return (
        <>
          {previewUrl ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                window.open(previewUrl, '_blank', 'noopener,noreferrer')
              }
              className="shrink-0"
            >
              <ExternalLink className="mr-1.5 h-4 w-4" />
              Open preview
            </Button>
          ) : null}
        </>
      );
    }

    return null;
  }, [
    actionLoading,
    activePanel,
    createChatSession,
    fetchGitHistory,
    gitState?.travelState?.mode,
    previewUrl,
    runGitAction,
  ]);

  useEffect(() => {
    const existingIds = new Set(chatSessions.map((item) => item.id));
    const nextSelected = selectedThreadIds.filter((id) => existingIds.has(id));
    if (nextSelected.length !== selectedThreadIds.length) {
      setSelectedThreadIds(nextSelected);
    }
  }, [chatSessions, selectedThreadIds, setSelectedThreadIds]);

  const filteredChatSessions = useMemo(() => {
    const keyword = threadSearch.trim().toLowerCase();
    if (!keyword) return chatSessions;
    return chatSessions.filter(
      (item) =>
        item.name.toLowerCase().includes(keyword) ||
        item.id.toLowerCase().includes(keyword),
    );
  }, [chatSessions, threadSearch]);

  const visibleChatSessions = useMemo(() => {
    return filteredChatSessions.slice(0, visibleThreadCount);
  }, [filteredChatSessions, visibleThreadCount]);

  const filteredHistoryCommits = useMemo(() => {
    const commits = gitState?.commits || [];
    const keyword = historySearch.trim().toLowerCase();
    if (!keyword) return commits;
    return commits.filter(
      (commit) =>
        commit.subject.toLowerCase().includes(keyword) ||
        commit.author.toLowerCase().includes(keyword) ||
        commit.shortHash.toLowerCase().includes(keyword) ||
        commit.hash.toLowerCase().includes(keyword),
    );
  }, [gitState?.commits, historySearch]);

  const visibleHistoryCommits = useMemo(() => {
    return filteredHistoryCommits.slice(0, visibleHistoryCount);
  }, [filteredHistoryCommits, visibleHistoryCount]);

  useEffect(() => {
    resetThreadWindow();
  }, [resetThreadWindow, threadSearch]);

  useEffect(() => {
    setVisibleThreadCount((current) => {
      if (current >= filteredChatSessions.length) return current;
      return Math.min(20, filteredChatSessions.length);
    });
  }, [filteredChatSessions.length, setVisibleThreadCount]);

  useEffect(() => {
    resetHistoryWindow();
  }, [historySearch, resetHistoryWindow]);

  useEffect(() => {
    setVisibleHistoryCount((current) => {
      if (current >= filteredHistoryCommits.length) return current;
      return Math.min(20, filteredHistoryCommits.length);
    });
  }, [filteredHistoryCommits.length, setVisibleHistoryCount]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <Skeleton className="h-[640px] rounded-[28px]" />
          <Skeleton className="h-[640px] rounded-[28px]" />
        </div>
      </div>
    );
  }

  if (!session || !isPmCapableRunner(session.runnerType)) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 text-center">
        <MessageSquareText className="h-10 w-10 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">
            This session does not support PM chat
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a running GIFT Agent session from Talk instead.
          </p>
        </div>
        <Button onClick={() => navigate('/talk')}>Back to Talk</Button>
      </div>
    );
  }

  if (session.status !== 'running') {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 text-center">
        <MessageSquareText className="h-10 w-10 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold">This PM chat is not running</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Start the session again from the project or sessions page, then
            reopen Talk.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/talk')}>Back to Talk</Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/sessions/${session.id}`)}
          >
            Open session details
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 top-[24px] z-10 overflow-hidden bg-background md:top-14">
      <TalkSessionHeader
        title={headerContent.title}
        subtitle={headerContent.subtitle}
        createdAt={session.createdAt}
        actionSlot={headerActionSlot}
      />

      <div className="h-[calc(100%-6rem)] overflow-hidden px-4 pb-[3.85rem] pt-4 sm:px-6 lg:px-8 md:pb-[4.25rem]">
        <div className={cn(activePanel === 'chat' ? 'block h-full' : 'hidden')}>
          <div className="min-w-0 h-full">
            <NativeTalkChat
              key={activeChatSessionId || session.id}
              sessionId={session.id}
              activeChatSessionId={activeChatSessionId}
              refreshKey={chatRefreshKey}
            />
          </div>
        </div>

        <div
          className={cn(activePanel === 'sessions' ? 'block h-full' : 'hidden')}
        >
          <div className="h-full overflow-y-auto">
            <ThreadsPanel
              chatSessionsLoading={chatSessionsLoading}
              chatSessions={chatSessions}
              filteredChatSessions={filteredChatSessions}
              visibleChatSessions={visibleChatSessions}
              activeChatSessionId={activeChatSessionId}
              actionLoading={actionLoading}
              threadSearch={threadSearch}
              selectedThreadIds={selectedThreadIds}
              renamingSessionId={renamingSessionId}
              renamingValue={renamingValue}
              onThreadSearchChange={setThreadSearch}
              onActivate={(chatSessionId) => {
                void activateChatSession(chatSessionId);
                setActivePanel('chat');
              }}
              onDelete={(chatSessionId) =>
                void deleteChatSession(chatSessionId)
              }
              onToggleSelected={toggleSelectedThreadId}
              onToggleSelectAllVisible={() => {
                const visibleIds = visibleChatSessions.map((item) => item.id);
                const allVisibleSelected = visibleIds.every((id) =>
                  selectedThreadIds.includes(id),
                );
                if (allVisibleSelected) {
                  setSelectedThreadIds(
                    selectedThreadIds.filter((id) => !visibleIds.includes(id)),
                  );
                  return;
                }
                setSelectedThreadIds(
                  Array.from(new Set([...selectedThreadIds, ...visibleIds])),
                );
              }}
              onStartRename={(chatSession) => {
                setRenamingSessionId(chatSession.id);
                setRenamingValue(chatSession.name);
              }}
              onRenamingValueChange={setRenamingValue}
              onCommitRename={(chatSession) => {
                const nextName = renamingValue;
                setRenamingSessionId(null);
                setRenamingValue('');
                void commitRename(chatSession, nextName);
              }}
              onCancelRename={() => {
                setRenamingSessionId(null);
                setRenamingValue('');
              }}
              onLoadMore={() => setVisibleThreadCount((count) => count + 20)}
            />
          </div>
        </div>

        <div
          className={cn(activePanel === 'history' ? 'block h-full' : 'hidden')}
        >
          <div className="h-full overflow-y-auto">
            <HistoryPanel
              loading={gitHistoryLoading}
              error={gitHistoryError}
              history={gitState}
              historySearch={historySearch}
              visibleCommits={visibleHistoryCommits}
              filteredCommitCount={filteredHistoryCommits.length}
              actionLoading={actionLoading}
              onHistorySearchChange={setHistorySearch}
              onLoadMore={() => setVisibleHistoryCount((count) => count + 20)}
              onJumpTo={handleJumpToCommit}
              onJumpBack={handleJumpBack}
            />
          </div>
        </div>
      </div>

      <TalkSessionBottomNav
        activePanel={activePanel}
        onChange={setActivePanel}
      />

      <ConfirmDialog
        open={!!pendingGitConfirm}
        onOpenChange={(open) => {
          if (!open && !actionLoading) {
            setPendingGitConfirm(null);
          }
        }}
        title={
          pendingGitConfirm?.kind === 'return'
            ? 'Return without saving?'
            : 'Jump without saving?'
        }
        description={
          pendingGitConfirm?.kind === 'return'
            ? 'You have unsaved changes while viewing an older version. Continue and try to bring them back to your current version?'
            : 'You have unsaved changes. Continue and temporarily store them before opening this version?'
        }
        confirmLabel="Continue"
        variant="default"
        loading={actionLoading === 'jump' || actionLoading === 'return'}
        onConfirm={() => {
          if (pendingGitConfirm?.kind === 'jump') {
            void jumpToCommit(pendingGitConfirm.commit).finally(() =>
              setPendingGitConfirm(null),
            );
            return;
          }
          if (pendingGitConfirm?.kind === 'return') {
            void jumpBack().finally(() => setPendingGitConfirm(null));
          }
        }}
      />
    </div>
  );
}
